import { Stack } from '@koob/margaret';
import { useField } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import { Container } from 'ui';
import ExperienceOptionsSelectField from '../Partials/ExperienceOptionsSelectField';
import LocalMultiSearchableSelectField from '../../../../components/Fields/LocalMultiSearchableSelectField';
import { EXPERIENCE_OPTIONS_SCHEMA } from '../Utilities/ExperienceValidationSchemas';
import { getExperiences } from 'api/node/experiences';
import { useParams } from 'react-router-dom';

function getFilteredOptions(options, programs) {
  if (!programs?.length || !options?.length) return options || [];

  const usedIds = new Set(
    programs.flatMap((program) => [
      ...(program.suggestedExperiences?.map((exp) => exp.id) || []),
      ...(program.includedExperiences?.map((exp) => exp.id) || []),
    ]),
  );

  return options.filter((option) => !usedIds.has(option.id));
}

function ExperienceOptionsPerPeriod({ periodIndex, currentLocale }) {
  const { experienceId } = useParams();
  const { t } = useTranslation('experiences');

  const [{ value: extras }, , { setValue: setExtras }] = useField(`extras`);
  const [{ value: period }, , { setValue: setPeriod }] = useField(
    `periods[${periodIndex}]`,
  );
  const [{ value: programs }] = useField(`programs`);
  const [{ value: incompatibilities }] = useField('incompatibilities');

  const { data, reload } = useAsync({
    promiseFn: getExperiences,
    locale: currentLocale.value,
    filters: {
      startAt: period.startAt,
      endAt: period.endAt,
      excludedExperiences: [
        experienceId,
        ...(incompatibilities ?? []).map(({ value }) => value),
      ],
      asOptions: true,
    },
  });

  useEffect(() => {
    if (data) {
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocale?.value]);

  const filteredOptions = useMemo(
    () => getFilteredOptions(data?.data, programs),
    [data, programs],
  );

  const options = (filteredOptions ?? []).map((experience) => {
    return {
      label: experience.name,
      value: experience.id,
    };
  });

  return (
    <Stack
      direction="column"
      gutterSize={1.5}
      size="full"
      className="p-4 border rounded-lg"
    >
      <span style={{ flex: '0 0 auto', marginBottom: '5px' }}>
        {t('steps.options.period', {
          startAt: period.startAt,
          endAt: period.endAt,
        })}
      </span>

      <LocalMultiSearchableSelectField
        label={''}
        name={`periods[${periodIndex}].extras`}
        options={options}
        renderOption={(option) => option.label}
        renderSelectedOption={(option) => option.label}
        onSelectOption={(option) => {
          if (extras.findIndex((extra) => extra.id === option.value) === -1) {
            setExtras(
              [
                ...extras,
                {
                  id: option.value,
                  name: option.label,
                  suggested: [],
                  required: [],
                },
              ],
              true,
            );
          }
          setPeriod({
            ...period,
            extras: [],
          });
        }}
      />
    </Stack>
  );
}

function ExtrasList(locale) {
  const { t } = useTranslation('experiences');
  const [{ value }, , { setValue }] = useField(`extras`);
  const [{ value: programs }] = useField(`programs`);
  const [{ value: incompatibilities }] = useField('incompatibilities');

  const filteredOptions = useMemo(
    () => getFilteredOptions(value, programs),
    [value, programs],
  );

  return (
    <Stack direction="column" style={{ flex: '1' }} size={'full'}>
      <div className="w-full border rounded-lg">
        <div className="px-4 py-3 border-b font-semibold">
          {t('steps.options.optionsName')}
        </div>

        <div className="flex-col divide-y">
          {filteredOptions?.map((extra) => {
            const index = value.findIndex((elem) => elem.id === extra.id);
            const requiredExtraSelected = value[index]?.required || [];
            const suggestedExtraSelected = value[index]?.suggested || [];

            return (
              <div
                key={extra?.id}
                className="p-4 flex justify-between space-x-3 items-start"
              >
                <details className="group py-1 text-sm w-full">
                  <summary className="flex cursor-pointer flex-row items-center justify-between w-full font-semibold text-gray-800 marker:[font-size:0px]">
                    <div className="font-medium text-base">{extra.name}</div>

                    <svg
                      className="h-6 w-6 rotate-0 transform text-gray-900 group-open:rotate-180"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </summary>

                  <div className="mt-3 pl-4 flex flex-col gap-4">
                    <ExperienceOptionsSelectField
                      label={t('steps.options.requiredOptions')}
                      name={`extras[${index}].required`}
                      query={getExperiences}
                      variables={{
                        types: ['Activity', 'Transfer'],
                        locale: locale.value,
                        asOptions: true,
                        excludedExperiences: [
                          extra?.id,
                          ...(incompatibilities ?? []).map(
                            ({ value }) => value,
                          ),
                          ...suggestedExtraSelected.map((opt) => opt.id),
                        ],
                      }}
                      clearFieldName="extras.required"
                    />

                    <ExperienceOptionsSelectField
                      label={t('steps.options.suggestedOptions')}
                      name={`extras[${index}].suggested`}
                      query={getExperiences}
                      variables={{
                        types: ['Activity', 'Transfer'],
                        locale: locale.value,
                        asOptions: true,
                        excludedExperiences: [
                          extra?.id,
                          ...(incompatibilities ?? []).map(
                            ({ value }) => value,
                          ),
                          ...requiredExtraSelected.map((opt) => opt.id),
                        ],
                      }}
                      clearFieldName="extras.suggested"
                    />
                  </div>
                </details>

                <div className="mt-1.5">
                  <i
                    className="fa-light fa-xmark cursor-pointer"
                    style={{ fontSize: 20 }}
                    onClick={() =>
                      setValue(value.filter((elem) => elem.id !== extra.id))
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Stack>
  );
}

export function ExperienceOptions({ currentLocale }) {
  const [{ value: periods }] = useField(`periods`);
  return (
    <Container size="full">
      <Stack direction="column" gutterSize={1.5} size="full">
        {periods.map((_, index) => (
          <ExperienceOptionsPerPeriod
            key={index}
            periodIndex={index}
            currentLocale={currentLocale}
          />
        ))}

        <ExtrasList locale={currentLocale}></ExtrasList>
      </Stack>
    </Container>
  );
}

export const getSchema = () => EXPERIENCE_OPTIONS_SCHEMA;
